import React, { useState } from "react";
import { 
  FiDollarSign, 
  FiCreditCard, 
  FiCalendar, 
  FiFileText, 
  FiHash,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiTrendingUp,
  FiShoppingBag,
  FiCheckCircle
} from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import { useCreatePaymentMutation, useDeletePaymentMutation, useGetOrdersQuery, useGetPaymentsQuery, useUpdatePaymentMutation } from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";
import { money, shortDate } from "../utils/formatters";

const emptyForm = { order_id: 1, amount: 0, payment_method: "bank_transfer", reference_number: "", notes: "" };

const PAYMENT_METHOD_ICONS = {
  cash: "💵",
  card: "💳",
  bank_transfer: "🏦",
  mobile_money: "📱",
};

const PAYMENT_METHOD_COLORS = {
  cash: { bg: "#dcfce7", color: "#16a34a" },
  card: { bg: "#dbeafe", color: "#2563eb" },
  bank_transfer: { bg: "#e0e7ff", color: "#6366f1" },
  mobile_money: { bg: "#fce7f3", color: "#ec4899" },
};

const PaymentsPage = () => {
  const { data } = useGetPaymentsQuery();
  const { data: orders } = useGetOrdersQuery();
  const [createPayment] = useCreatePaymentMutation();
  const [updatePayment] = useUpdatePaymentMutation();
  const [deletePayment] = useDeletePaymentMutation();
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const payments = data?.data || [];
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);
    try {
      if (editingId) {
        await updatePayment({ id: editingId, ...form }).unwrap();
      } else {
        await createPayment(form).unwrap();
      }
      setEditingId(null);
      setForm(emptyForm);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to record payment."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredPayments = payments.filter((payment) =>
    payment.reference_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.payment_method.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.order_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const totalPayments = payments.reduce((sum, p) => sum + Number(p.amount), 0);
  const paymentsByMethod = payments.reduce((acc, p) => {
    acc[p.payment_method] = (acc[p.payment_method] || 0) + Number(p.amount);
    return acc;
  }, {});

  const getOrderTitle = (orderId) => {
    const order = orders?.data?.find(o => o.id === orderId);
    return order ? order.title : `Order #${orderId}`;
  };

  return (
    <div className="dashboard-page">
      <SectionHeader eyebrow="Payments" title="Track multiple payments per order and follow cash collection." />
      <div className="dashboard-grid dashboard-grid--wide">
        <section className="panel">
          <h3>Payment ledger</h3>
          {data?.data?.map((payment) => (
            <div key={payment.id} className="list-row">
              <div>
                <strong>{payment.reference_number || `Payment #${payment.id}`}</strong>
                <p>{shortDate(payment.created_at)} • {payment.payment_method}</p>
              </div>
              <div className="mini-metrics">
                <strong>{money(payment.amount)}</strong>
                <button className="table-action" type="button" onClick={() => { setEditingId(payment.id); setForm(payment); }}>Edit</button>
                <button className="table-action table-action--danger" type="button" onClick={() => deletePayment(payment.id)}>Delete</button>
              </div>
            </div>
          ))}
        </section>

        <form className="panel form-panel" onSubmit={handleSubmit}>
          <h3>{editingId ? "Edit payment" : "Add payment"}</h3>
          <select value={form.order_id} onChange={(e) => setForm({ ...form, order_id: Number(e.target.value) })}>
            {orders?.data?.map((order) => (
              <option key={order.id} value={order.id}>{order.title}</option>
            ))}
          </select>
          <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} placeholder="Amount" />
          <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="mobile_money">Mobile Money</option>
          </select>
          <input
            value={form.reference_number}
            onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
            placeholder="Reference"
          />
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows="4" />
          <button className="primary-button" type="submit">{editingId ? "Update payment" : "Record payment"}</button>
          {editingId ? <button className="secondary-button" type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancel</button> : null}
          {submitError ? <small className="danger-text">{submitError}</small> : null}
        </form>
      </div>
    </div>
  );
};

export default PaymentsPage;
